import React, { useState, useRef, useEffect } from 'react';
import SearchIcon from '../Icons/SearchIcon';
import DropDownIcon from '../Icons/DropDownIcon';
import styles from './MultiSelectDropdown.module.css';

const MultiSelectDropdown = ({
    options,
    selectedValues = [],
    onChange,
    placeholder = "Select options",
    allSelectedText,
    disabled = false,
    searchPlaceholder = "Search...",
    onScrollToBottom,
    onOpen,
    loading = false,
    hasMore = false,
    iconColor = "#6b7280",
    iconStrokeWidth = "1",
    textColor = "var(--neutral-500)",
    resetSearchTrigger,
    showOptionAvatar = true,
    autoFocusSearch = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const optionsListRef = useRef(null);
    const searchInputRef = useRef(null);

    // Calculate derived values early
    const filteredOptions = options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Hide infinite scroll when searching locally
    const shouldShowInfiniteScroll = !searchTerm.trim() && hasMore;
    
    // Simple debug when needed (can be removed later)
  

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Notify parent on open so it can scroll the container
    useEffect(() => {
        if (isOpen && typeof onOpen === 'function') {
            onOpen();
        }
    }, [isOpen, onOpen]);

    useEffect(() => {
        if (!isOpen || !autoFocusSearch) return;

        const focusTimeout = setTimeout(() => {
            searchInputRef.current?.focus();
            searchInputRef.current?.select();
        }, 0);

        return () => clearTimeout(focusTimeout);
    }, [isOpen, autoFocusSearch]);

    // Clear search input when triggered by parent
    useEffect(() => {
        if (resetSearchTrigger !== undefined) {
            setSearchTerm('');
        }
    }, [resetSearchTrigger]);

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    const handleOptionClick = (optionValue) => {
        const newSelectedValues = selectedValues.includes(optionValue)
            ? selectedValues.filter(value => value !== optionValue)
            : [...selectedValues, optionValue];
        onChange(newSelectedValues);
    };

    // Select All always toggles the full option list; search only filters visible rows.
    const handleSelectAll = () => {
        const allOptionValues = options.map(option => option.value);
        const isAllSelected =
            allOptionValues.length > 0 &&
            allOptionValues.every(value => selectedValues.includes(value));

        onChange(isAllSelected ? [] : allOptionValues);
    };

    const selectedOptions = options.filter(option => selectedValues.includes(option.value));
    const isAllSelected = options.length > 0 && selectedOptions.length === options.length;
    const displayText = isAllSelected
        ? (allSelectedText || `${selectedOptions.length} selected`)
        : (selectedOptions.length > 0 
            ? `${selectedOptions.length} selected`
            : placeholder);

    const getGenderColor = (gender) => {
        return gender === 'male' ? '#3B82F6' : '#EC4899'; // Blue for male, pink for female
    };

    return (
        <div className={styles.dropdownContainer} ref={dropdownRef}>
            <div
                className={`${styles.dropdownTrigger} ${disabled ? styles.disabled : ''}`}
                onClick={handleToggle}
                style={{ color: textColor }}
            >
                <span className={styles.selectedValue}>
                    {displayText}
                </span>
                <DropDownIcon
                    size={12}
                    color={iconColor}
                    strokeWidth={iconStrokeWidth}
                    className={`${styles.dropdownIcon} ${isOpen ? styles.rotated : ''}`}
                />
            </div>

            {isOpen && (
                <div className={styles.dropdownMenu}>
                    {/* Search Bar */}
                    <div className={styles.searchContainer}>
                        <SearchIcon />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.searchInput}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus={autoFocusSearch}
                        />
                    </div>

                    {/* Select All Option */}
                    {!loading && filteredOptions.length > 0 && (
                        <div
                            className={styles.selectAllOption}
                            onClick={handleSelectAll}
                        >
                            <label
                                className="checkbox"
                                style={{ width: 'auto' }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    onChange={handleSelectAll}
                                    className={styles.hiddenCheckbox}
                                />
                                <span className="checkmark icn check" />
                            </label>
                            <span className={styles.selectAllText}>Select All</span>
                        </div>
                    )}

                    {/* Options List */}
                    <div className={styles.optionsList} ref={optionsListRef}>
                        {/* Show loading indicator prominently at the top */}
                        {loading && (
                            <div className={styles.loadingContainer}>
                                <div className={styles.loadingText}>Loading therapists...</div>
                            </div>
                        )}
                        
                        {!loading && filteredOptions.map((option) => (
                            <div
                                key={option.value}
                                className={styles.dropdownOption}
                                onClick={() => handleOptionClick(option.value)}
                            >
                                <label 
                                    className="checkbox" 
                                    style={{ width: 'auto' }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedValues.includes(option.value)}
                                        onChange={() => handleOptionClick(option.value)}
                                        className={styles.hiddenCheckbox}
                                    />
                                    <span className="checkmark icn check" />
                                </label>
                                {showOptionAvatar && (
                                    <div
                                        className={styles.avatar}
                                        style={{ backgroundColor: getGenderColor(option.gender) }}
                                    >
                                        {option.pagerNumber || option.id}
                                    </div>
                                )}
                                <span className={styles.optionLabel}>
                                    {option.label}
                                    {option.isStarred && '*'}
                                </span>
                            </div>
                        ))}
                    </div>

                    {!loading && filteredOptions.length === 0 && (
                        <div className={styles.noResults}>
                            {searchTerm.trim() ? 'No therapists found matching your search' : 'No therapists available'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MultiSelectDropdown;
